import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { ensureRolesLoaded, initializeAuthState } from '@/services/authSession'
import { pinia } from '@/stores/pinia'
import { useEventUserRolesStore } from '@/stores/eventUserRoles'
import { useUserStore } from '@/stores/user'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
  },
  {
    path: '/success',
    name: 'success',
    component: () => import('@/views/Success.vue'),
  },
  {
    path: '/cancel',
    name: 'cancel',
    component: () => import('@/views/Cancel.vue'),
  },
  {
    path: '/transaction/:id',
    name: 'transaction',
    component: () => import('@/views/Transaction.vue'),
  },
  {
    path: '/mes-evenements',
    name: 'mes-evenements',
    component: () => import('@/views/MesEvenements.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile/change-password',
    name: 'change-password',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reglages-avances',
    name: 'advanced-settings',
    component: () => import('@/views/AdvancedSettings.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/admin/Admin.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/event/new',
    name: 'admin-event-new',
    component: () => import('@/views/admin/EventNew.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/event/:slug',
    name: 'admin-event',
    component: () => import('@/views/admin/EventView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/event/edit/:slug',
    name: 'admin-event-edit',
    component: () => import('@/views/admin/EventEdit.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/event/:slug/survey/:id',
    name: 'admin-survey',
    component: () => import('@/views/admin/Survey.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/:slug',
    name: 'event',
    component: () => import('@/views/Event.vue'),
  },
  {
    path: '/:slug/survey/:id',
    name: 'survey',
    component: () => import('@/views/Survey.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const userStore = useUserStore(pinia)
  const eventUserRolesStore = useEventUserRolesStore(pinia)

  if (!to.meta.requiresAuth && !to.meta.requiresAdmin) {
    return true
  }

  await initializeAuthState()

  if (to.meta.requiresAuth && !userStore.token) {
    return {
      name: 'login',
      query: {
        redirect: to.fullPath,
      },
    }
  }

  if (to.meta.requiresAdmin) {
    // The fast path in initializeAuthState() skips loading event roles when a
    // valid JWT is already in storage, so on a direct navigation / page refresh
    // eventRoles is still empty here. Global admins are recognised from the JWT
    // alone, but event-level admins are not — without this they get bounced to
    // home. Load the roles before deciding (skipped for global admins).
    if (!userStore.isAdmin) {
      // Don't let a roles-fetch failure abort navigation outright; fall through
      // to the check below (which sends a non-admin to home) instead.
      try {
        await ensureRolesLoaded()
      } catch (error) {
        console.error('Failed to load event roles for admin route guard', error)
      }
    }
    const isAdmin = userStore.isAdmin || eventUserRolesStore.eventRoles.some((role) => role.role === 'ADMIN')
    if (!isAdmin) {
      return { name: 'home' }
    }
  }

  return true
})

export default router
